import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { ScoreRing } from "@/components/Badges";
import { ShieldCheck, MapPin, Building2, Wallet } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export default function Matching() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api.get("/matching").then((r) => setData(r.data));
  }, []);

  const current = data[active];

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 lg:px-12" data-testid="matching-page">
      <header className="mb-8">
        <p className="overline text-[#C6A87C]">{t("matching.overline")}</p>
        <h1 className="mt-2 font-display text-4xl tracking-tight text-[#0F172A] sm:text-5xl">{t("matching.title")}</h1>
        <p className="mt-2 text-slate-500">
          {t("matching.subtitle")}
        </p>
      </header>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        {/* Opportunity selector */}
        <div className="lg:col-span-4">
          <p className="overline mb-3 text-slate-400">{t("matching.opportunities")}</p>
          <div className="space-y-2">
            {data.map((o, i) => (
              <button
                key={o.opportunity_id}
                data-testid={`match-opp-${o.opportunity_id}`}
                onClick={() => setActive(i)}
                className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-all ${
                  active === i
                    ? "border-[#C6A87C] bg-white shadow-sm"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                {o.photo ? (
                  <img src={fileUrl(o.photo)} alt="" className="h-12 w-12 rounded-lg object-cover" />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100 text-slate-300">
                    <Building2 className="h-5 w-5" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-[#0F172A]">{o.title}</p>
                  <p className="flex items-center gap-1 text-xs text-slate-400">
                    <MapPin className="h-3 w-3" /> {o.city}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Matches */}
        <div className="lg:col-span-8">
          {current && (
            <>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-display text-2xl text-[#0F172A]">
                  {t("matching.compatibleInvestors")}
                </h2>
                <span className="text-sm text-slate-400">{current.property_type} · {current.country}</span>
              </div>
              <div className="space-y-3">
                {current.matches.map((m, i) => (
                  <motion.div
                    key={m.investor_id}
                    initial={{ opacity: 0, x: 14 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    data-testid={`match-investor-${m.investor_id}`}
                    className="rounded-2xl border border-slate-200 bg-white p-4 transition-all hover:shadow-sm"
                  >
                    <div className="flex items-center gap-4">
                      <img src={m.avatar} alt="" className="h-12 w-12 rounded-full object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-2 font-semibold text-[#0F172A]">
                          {m.investor_name}
                          {m.nda_signed && <ShieldCheck className="h-4 w-4 text-[#C6A87C]" />}
                        </p>
                        <p className="text-sm text-slate-400">{m.country}</p>
                      </div>
                      <div className="hidden flex-1 sm:block">
                        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${m.score}%`,
                              background: m.score >= 70 ? "#C6A87C" : "#94A3B8",
                            }}
                          />
                        </div>
                        <p className="mt-1 text-xs text-slate-400">
                          {m.score >= 70 ? t("matching.excellent") : m.score >= 40 ? t("matching.medium") : t("matching.low")}
                        </p>
                      </div>
                      <ScoreRing score={m.score} />
                    </div>

                    {m.breakdown && (
                      <div className="mt-4 grid grid-cols-3 gap-3 border-t border-slate-100 pt-4" data-testid={`match-breakdown-${m.investor_id}`}>
                        {[
                          { key: "type", icon: Building2, label: t("matching.axisType"), val: m.breakdown.type, max: 35 },
                          { key: "location", icon: MapPin, label: t("matching.axisLocation"), val: m.breakdown.location, max: 35 },
                          { key: "budget", icon: Wallet, label: t("matching.axisBudget"), val: m.breakdown.budget, max: 30 },
                        ].map((ax) => {
                          const pct = Math.round((ax.val / ax.max) * 100);
                          const reason = (m.reasons || []).find((r) => r.axis === ax.key);
                          const strong = pct >= 80;
                          return (
                            <div key={ax.key} className="min-w-0">
                              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
                                <ax.icon className="h-3.5 w-3.5" />
                                <span>{ax.label}</span>
                                <span className="ml-auto tabular-nums text-slate-400">{ax.val}/{ax.max}</span>
                              </div>
                              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
                                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: strong ? "#C6A87C" : "#CBD5E1" }} />
                              </div>
                              {reason && <p className="mt-1.5 text-[11px] leading-tight text-slate-400">{reason.label}</p>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
